import { Paintbrush, ShieldCheck, ThermometerSun } from "lucide-react";
import Booking from "./Booking";
import ServicePageFaqSection from "./ServicePageFaqSection";
import { useFaqPageJsonLd } from "@/hooks/useFaqPageJsonLd";

export default function PaintPage({ t }) {
  useFaqPageJsonLd(t.paintPage.faq, "service-paint-faq");

  return (
    <article
      id="paint-page"
      data-testid="paint-page"
      className="bg-white border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <header>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-4">
            {t.paintPage.eyebrow}
          </div>
          <h1 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {t.paintPage.title}
          </h1>
          <p className="mt-6 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
            {t.paintPage.lead}
          </p>
          <p className="mt-4 max-w-4xl text-slate-600 leading-relaxed">{t.paintPage.leadExtended}</p>
          {t.paintPage.secondary ? (
            <p className="mt-5 max-w-4xl text-slate-600 leading-relaxed text-base sm:text-lg">
              {t.paintPage.secondary}
            </p>
          ) : null}
        </header>

        <div className="mt-8 border border-[#e2e6ec] bg-white overflow-hidden">
          <img
            src="/resources/paint-hero.png"
            alt={t.paintPage.heroImageAlt}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>

        <section className="mt-12" aria-labelledby="paint-cards-heading">
          <h2
            id="paint-cards-heading"
            className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-2xl sm:text-3xl mb-8 max-w-4xl leading-tight"
          >
            {t.paintPage.cardsSectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <Paintbrush className="w-4 h-4" />
                {t.paintPage.card1Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.paintPage.card1Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <ShieldCheck className="w-4 h-4" />
                {t.paintPage.card2Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.paintPage.card2Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <ThermometerSun className="w-4 h-4" />
                {t.paintPage.card3Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.paintPage.card3Text}</p>
            </div>
          </div>
        </section>

        <div className="mt-8 border border-[#e2e6ec] bg-[#f7f8fa] p-6">
          <h2 className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] text-3xl tracking-tight">
            {t.paintPage.processTitle}
          </h2>
          <ul className="mt-4 space-y-3">
            {t.paintPage.processSteps.map((step, i) => (
              <li key={i} className="text-slate-700 leading-relaxed flex items-start gap-2">
                <span className="text-[#0b378c] mt-1" aria-hidden="true">
                  •
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8 border border-[#e2e6ec] bg-white p-6">
          <h2 className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] text-3xl tracking-tight">
            {t.paintPage.qualityTitle}
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">{t.paintPage.qualityText}</p>
        </div>

        <ServicePageFaqSection
          headingId="paint-faq-heading"
          title={t.paintPage.faqTitle}
          faq={t.paintPage.faq}
        />

        <div className="mt-12">
          <Booking
            t={t}
            initialService={t.services.items[2]?.title || ""}
            copyOverrides={t.paintPage.bookingCopy}
          />
        </div>
      </div>
    </article>
  );
}
